import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import GlobalContext from 'contexts/GlobalContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { HeadsUpIntroConfig, useFeatureFlag, useFeatureFlagsReady } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { isBiometricEnrollmentOfferPending } from 'hooks/useBiometricEnrollmentOffer';
import { setFeatureIntroModalOpen, setFeatureIntroSlot } from 'reducers/playerPromos';

import {
  canOpenIntro,
  decideIntroSlot,
  FEATURE_INTRO_BIOMETRIC_OFFER_WAIT_MS,
  FEATURE_INTRO_BIOMETRIC_RECHECK_MS,
  isInLivePlay,
  mustTearDownIntro
} from 'components/FeatureIntro/featureIntroGate';
import { adaptActiveTables } from 'components/SaloonLobby/useActiveTables';

import { reportHeadsUpIntroEvent } from './headsUpIntroAnalytics';
import { resolveHeadsUpIntroContent } from './headsUpIntroCopy';
import HeadsUpIntroModal, { HeadsUpIntroDismissReason } from './HeadsUpIntroModal';
import { followHeadsUpIntroDismissal } from './headsUpIntroNavigation';
import { markHeadsUpIntroSeen, readHeadsUpIntroSeen } from './headsUpIntroSeen';

/**
 * Presents the one-time heads-up Sit & Go introduction (EN-10748): the first
 * time a player logs in with heads-up launched, once, and never again after
 * they dismiss it. The multi-run introduction's logic (EN-10791,
 * MultiRunIntroAutoPrompt), with its rules shared through
 * components/FeatureIntro:
 *   - the trigger is the `release.heads-up-intro` flag, whose value is an
 *     EDITION (heads-up-launch, …): the copy to show, and the seen key it is
 *     shown once under;
 *   - "seen" is written when the player dismisses it;
 *   - it shares the session's ONE login-modal slot with the promo entry modal
 *     and the multi-run introduction, and is never presented with either.
 *
 * Precedence, newest introduction first (EN-10748 §1.4): the older
 * introductions are passed as children, and they are mounted only once this
 * one has PASSED on the session — the flag off, the edition seen, storage
 * unreadable, or the flag switched off before it ever showed. While this one
 * is deciding (the flags evaluating) or holds the session, they are not
 * mounted at all, so they decide nothing, show nothing, and get the next
 * session instead. One introduction per session, never two.
 *
 * The slot is decided as soon as the player's flags are known
 * (useFeatureFlagsReady — bounded, so the promo is never held for long) and
 * held for the session: the modal then waits for the first moment it may open
 * — the lobby, loaded, a valid location, after any biometric enrollment offer,
 * never over live play at another table — which can be later in the session.
 *
 * The introduction counts as ON only while the Sit & Go tab can show Heads-up:
 * its own flag serving an edition AND `release.sit-go-seat-toggle` on for this
 * player (EN-10748 §1.5). FIND A HEADS-UP TABLE lands with Heads-up lit, and
 * without the toggle there is nothing to light; a player whose tab is still
 * the old ladder is not introduced, and does not burn their one showing. The
 * rollout order (this flag flipped last) is the first control; this is the
 * second.
 *
 * The edition is PINNED when the slot is decided: the player sees exactly that
 * edition and it is exactly that edition that is marked seen, even if the
 * flag's variation changes while they wait or read. Only a switch to off (of
 * either flag) ends it early. The next session picks up the new edition.
 */
type HeadsUpIntroAutoPromptProps = {
  /**
   * The biometric enrollment sheet (or its confirmation beat) is on screen.
   * App.tsx owns that offer's state; the introduction never stacks on it.
   */
  biometricPromptOpen?: boolean;
  /** The older introductions, mounted only once this one has passed on the session. */
  children?: ReactNode;
};

type FinishOutcome = 'dismissed' | 'interrupted' | 'released';

const HeadsUpIntroAutoPrompt = ({
  biometricPromptOpen = false,
  children = null
}: HeadsUpIntroAutoPromptProps) => {
  const dispatch = useAppDispatch();
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const guest = useAppSelector((state) => state.user.guest);
  const slot = useAppSelector((state) => state.playerPromos.featureIntroSlot);
  const flagsReady = useFeatureFlagsReady();
  const config = useFeatureFlag('headsUpIntro');
  const sitgoSeatToggle = useFeatureFlag('sitgoSeatToggle');
  const flagOn = config !== null && sitgoSeatToggle;
  const identity = !guest && playerGUID ? playerGUID : null;

  // Who this session's slot was decided for, and the edition it was decided
  // on (null when the introduction does not take the session); the player
  // whose attempt this session is over (dismissed, torn down, or released);
  // and the player it was released for without showing, so the older
  // introductions may have the session after all.
  const [decided, setDecided] = useState<{
    identity: string;
    config: HeadsUpIntroConfig | null;
  } | null>(null);
  const [finishedFor, setFinishedFor] = useState<string | null>(null);
  const [releasedFor, setReleasedFor] = useState<string | null>(null);

  // Decide the slot once per player per session, the moment the player is
  // known. Login sets playerGUID before the promos are even requested (App.tsx,
  // GOT_LOGIN), so by the time the promo has anything to show it already reads
  // 'pending' or the decision. Deciding in a layout effect puts that in the
  // store before the next paint, and mounts the older introductions (when this
  // one passes) in the same beat, so their own decision lands before the promo
  // re-reads the slot.
  useLayoutEffect(() => {
    if (!identity) {
      // Logging out ends the session: the next login is a fresh attempt, so
      // an introduction that was interrupted this session is offered again.
      if (decided !== null) setDecided(null);
      if (finishedFor !== null) setFinishedFor(null);
      if (releasedFor !== null) setReleasedFor(null);
      if (slot !== 'none') dispatch(setFeatureIntroSlot('none'));
      return;
    }
    if (decided?.identity === identity) return;

    const next = decideIntroSlot({
      identity,
      flagsReady,
      flagOn,
      seen: config ? readHeadsUpIntroSeen(config.edition, identity) : 'unseen'
    });
    if (next !== slot) dispatch(setFeatureIntroSlot(next));
    // 'pending' is re-decided when the flags land; anything else is final
    // for the session (a flag flipped on mid-session waits for the next one).
    // A reservation pins the edition it was decided on.
    if (next !== 'pending') setDecided({ identity, config: next === 'reserved' ? config : null });
  }, [identity, flagsReady, config, flagOn, slot, decided, finishedFor, releasedFor, dispatch]);

  const handleFinished = useCallback(
    (outcome: FinishOutcome) => {
      setFinishedFor(identity);
      // The flag went off before the introduction ever showed: hand the
      // session back, to the older introductions first and then the promo.
      if (outcome === 'released') {
        setReleasedFor(identity);
        dispatch(setFeatureIntroSlot('none'));
      }
    },
    [identity, dispatch]
  );

  const pinned = decided?.identity === identity ? decided.config : null;
  const contending =
    identity !== null && pinned !== null && slot === 'reserved' && finishedFor !== identity;
  // Passed on the session: decided against it, or released it unshown. Not
  // while it is pending, and not after it showed, whatever the outcome.
  const passed =
    identity !== null &&
    decided?.identity === identity &&
    (decided.config === null || releasedFor === identity);

  return (
    <>
      {contending && (
        <HeadsUpIntroPresenter
          identity={identity}
          config={pinned}
          flagOn={flagOn}
          biometricPromptOpen={biometricPromptOpen}
          onFinished={handleFinished}
        />
      )}
      {passed ? children : null}
    </>
  );
};

type HeadsUpIntroPresenterProps = {
  identity: string;
  /** The edition pinned when the slot was decided: what shows, and what is marked seen. */
  config: HeadsUpIntroConfig;
  /** Both flags right now (the edition, and the seat toggle). A switch to off ends an attempt early. */
  flagOn: boolean;
  biometricPromptOpen: boolean;
  onFinished: (outcome: FinishOutcome) => void;
};

/**
 * Mounted only while the introduction holds the slot and has not finished, so
 * the table subscription it needs for the live-play gate costs nothing for the
 * rest of a session (or for every session after the player has seen it).
 */
const HeadsUpIntroPresenter = ({
  identity,
  config,
  flagOn,
  biometricPromptOpen,
  onFinished
}: HeadsUpIntroPresenterProps) => {
  const dispatch = useAppDispatch();
  const isLobbyReady = useAppSelector((state) => state.lobby.isLobbyReady);
  const promoEntryModalOpen = useAppSelector((state) => state.playerPromos.promoEntryModalOpen);
  const playerGames = useAppSelector((state) => state.playerGames);
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  const { activeView } = useContext(ViewManagerContext);
  const { isLocationInvalid } = useContext(GlobalContext);
  const [open, setOpen] = useState(false);
  const [, setRecheck] = useState(0);
  const presentedFromRef = useRef(Date.now());
  const content = useMemo(() => resolveHeadsUpIntroContent(config), [config]);
  // Report the outcome exactly once, and only give the session back if the
  // introduction never reached the screen.
  const shownRef = useRef(false);
  const finishedRef = useRef(false);
  const finish = useCallback(
    (outcome: FinishOutcome) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      onFinished(outcome);
    },
    [onFinished]
  );

  // Same derivation the dock uses (useActiveTables), without that hook's
  // ordering side effects.
  const inLivePlay = useMemo(
    () => isInLivePlay(adaptActiveTables(playerGames || [], tables || {})),
    [playerGames, tables]
  );
  const biometricOfferPending =
    isBiometricEnrollmentOfferPending() &&
    Date.now() - presentedFromRef.current < FEATURE_INTRO_BIOMETRIC_OFFER_WAIT_MS;
  const biometricPromptActive = biometricPromptOpen || biometricOfferPending;
  const gatesPass =
    flagOn &&
    canOpenIntro({
      activeView,
      isLobbyReady: Boolean(isLobbyReady),
      isLocationInvalid,
      biometricPromptActive,
      inLivePlay,
      promoEntryModalOpen: Boolean(promoEntryModalOpen)
    });

  // The flag went off before it showed.
  useEffect(() => {
    if (!shownRef.current && !flagOn) finish('released');
  }, [flagOn, finish]);

  useEffect(() => {
    if (open || shownRef.current || !gatesPass) return;
    shownRef.current = true;
    setOpen(true);
    reportHeadsUpIntroEvent({ name: 'heads_up_intro_shown', edition: config.edition }, identity);
  }, [open, gatesPass, config.edition, identity]);

  // Close WITHOUT counting as seen if live play, the location, the view, the
  // flag or the biometric sheet changes underneath it. That ends this
  // session's attempt (no flicker back open); the player gets it on the next
  // session. It is still reported, once, as a dismissal whose reason is
  // 'interrupted', so a showing never goes missing from the data.
  useEffect(() => {
    if (!open) return;
    if (
      mustTearDownIntro({ activeView, isLocationInvalid, inLivePlay, flagOn, biometricPromptOpen })
    ) {
      setOpen(false);
      if (!finishedRef.current) {
        reportHeadsUpIntroEvent(
          { name: 'heads_up_intro_dismissed', edition: config.edition, reason: 'interrupted' },
          identity
        );
      }
      finish('interrupted');
    }
  }, [
    identity,
    open,
    activeView,
    isLocationInvalid,
    inLivePlay,
    flagOn,
    biometricPromptOpen,
    config.edition,
    finish
  ]);

  useEffect(() => {
    if (open || biometricPromptOpen || !biometricOfferPending) return undefined;
    const timer = window.setInterval(
      () => setRecheck((n) => n + 1),
      FEATURE_INTRO_BIOMETRIC_RECHECK_MS
    );
    return () => window.clearInterval(timer);
  }, [open, biometricPromptOpen, biometricOfferPending]);

  // The ticker holds its rotation while this is open, as it does for the promo.
  useEffect(() => {
    dispatch(setFeatureIntroModalOpen(open));
  }, [dispatch, open]);
  useEffect(
    () => () => {
      dispatch(setFeatureIntroModalOpen(false));
    },
    [dispatch]
  );

  // Dismiss first, then follow: the modal is gone before the lobby is asked
  // for the Sit & Go tab (Jason, 2026-10-26).
  const handleDismiss = useCallback(
    (reason: HeadsUpIntroDismissReason) => {
      // Best effort: if storage refuses the write, finishing still ends this
      // session's attempt, so it cannot repeat until the next login.
      markHeadsUpIntroSeen(config.edition, identity);
      reportHeadsUpIntroEvent(
        { name: 'heads_up_intro_dismissed', edition: config.edition, reason },
        identity
      );
      setOpen(false);
      finish('dismissed');
      followHeadsUpIntroDismissal(reason);
    },
    [identity, config.edition, finish]
  );

  if (!open) return null;

  return <HeadsUpIntroModal onDismiss={handleDismiss} content={content} />;
};

export default HeadsUpIntroAutoPrompt;
